'use client';

import type {
  OrgChatArtifactPanelState,
  OrgChatAttachmentInput,
  OrgChatPresetDataInput,
} from '@/components/org-chat/org-chat-types';
import { normalizeAutoSendMessage } from '@/contexts/thread-startup-message';
import type { Id } from '@/convex/_generated/dataModel';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export type PendingThreadMessage = Partial<OrgChatPresetDataInput> & {
  prompt: string;
  rawUserPrompt?: string;
  startupMode?: 'auto-send' | 'draft';
  attachments?: Array<{
    kind?: OrgChatAttachmentInput['kind'];
    filename: string;
    mimeType: string;
    sizeBytes: number;
    storageKey?: string;
    url?: string;
    storageId?: Id<'_storage'>;
    workspaceFileId?: Id<'workspaceFiles'>;
    vfsPath?: string;
    previewUrl?: string;
    uploadId?: string;
    source?: OrgChatAttachmentInput['source'];
    referenceCategory?: OrgChatAttachmentInput['referenceCategory'];
    customReferenceCategoryId?: OrgChatAttachmentInput['customReferenceCategoryId'];
    mediaType?: OrgChatAttachmentInput['mediaType'];
  }>;
  presetId?: Id<'globalProductPresets'> | null;
  fixedModelAlias?: OrgChatPresetDataInput['fixedModelAlias'];
  artifactPanelState?: OrgChatArtifactPanelState;
};

export type ThreadNavigationHandoff = {
  prompt?: string;
  title?: string;
  attachments?: PendingThreadMessage['attachments'];
  startupPreviewTiles?: Array<{ src: string; label: string }>;
  artifactPanelState?: OrgChatArtifactPanelState;
  updatedAt?: number;
};

type ThreadStartupContextValue = {
  navigationHandoffs: ReadonlyMap<string, ThreadNavigationHandoff>;
  setPendingForThread: (threadId: string, data: PendingThreadMessage) => void;
  hasPendingForThread: (threadId: string) => boolean;
  peekPendingForThread: (threadId: string) => PendingThreadMessage | null;
  consumePendingForThread: (threadId: string) => PendingThreadMessage | null;
  setThreadNavigationHandoff: (
    threadId: string,
    data: ThreadNavigationHandoff
  ) => void;
  hasThreadNavigationHandoff: (threadId: string) => boolean;
  peekThreadNavigationHandoff: (
    threadId: string
  ) => ThreadNavigationHandoff | null;
  consumeThreadNavigationHandoff: (
    threadId: string
  ) => ThreadNavigationHandoff | null;
};

const ThreadStartupContext = createContext<
  ThreadStartupContextValue | undefined
>(undefined);

const THREAD_STARTUP_STORAGE_KEY = 'imai:thread-startup-pending:v1';
const THREAD_NAVIGATION_HANDOFF_STORAGE_KEY =
  'imai:thread-navigation-handoff:v1';
export const THREAD_NAVIGATION_HANDOFF_STALE_MS = 2 * 60 * 1000;

export function pruneExpiredThreadNavigationHandoffs(
  handoffs: ReadonlyMap<string, ThreadNavigationHandoff>,
  now: number
): Map<string, ThreadNavigationHandoff> {
  return new Map(
    Array.from(handoffs).filter(([, handoff]) => {
      const updatedAt = handoff.updatedAt ?? now;
      return now - updatedAt <= THREAD_NAVIGATION_HANDOFF_STALE_MS;
    })
  );
}

export function threadNavigationHandoffExpiryDelay(
  handoffs: ReadonlyMap<string, ThreadNavigationHandoff>,
  now: number
): number | null {
  if (handoffs.size === 0) return null;
  const expiresAt = Math.min(
    ...Array.from(
      handoffs.values(),
      (handoff) =>
        (handoff.updatedAt ?? now) + THREAD_NAVIGATION_HANDOFF_STALE_MS
    )
  );
  return Math.max(0, expiresAt - now);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readPendingThreadMessagesFromSessionStorage(): Map<
  string,
  PendingThreadMessage
> {
  if (typeof window === 'undefined') return new Map();

  try {
    const raw = window.sessionStorage?.getItem(THREAD_STARTUP_STORAGE_KEY);
    if (!raw) return new Map();
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return new Map();

    const next = new Map<string, PendingThreadMessage>();
    for (const [threadId, value] of Object.entries(parsed)) {
      if (!threadId || !isRecord(value) || typeof value.prompt !== 'string') {
        continue;
      }
      next.set(threadId, value as PendingThreadMessage);
    }
    return next;
  } catch {
    return new Map();
  }
}

function writePendingThreadMessagesToSessionStorage(
  pending: Map<string, PendingThreadMessage>
) {
  if (typeof window === 'undefined') return;

  try {
    if (pending.size === 0) {
      window.sessionStorage?.removeItem(THREAD_STARTUP_STORAGE_KEY);
      return;
    }
    window.sessionStorage?.setItem(
      THREAD_STARTUP_STORAGE_KEY,
      JSON.stringify(Object.fromEntries(pending))
    );
  } catch {
    // Ignore storage failures. The in-memory map still covers normal SPA nav.
  }
}

function readThreadNavigationHandoffsFromSessionStorage(): Map<
  string,
  ThreadNavigationHandoff
> {
  if (typeof window === 'undefined') return new Map();

  try {
    const raw = window.sessionStorage?.getItem(
      THREAD_NAVIGATION_HANDOFF_STORAGE_KEY
    );
    if (!raw) return new Map();
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return new Map();

    const now = Date.now();
    const next = new Map<string, ThreadNavigationHandoff>();
    for (const [threadId, value] of Object.entries(parsed)) {
      if (!threadId || !isRecord(value)) continue;
      const updatedAt =
        typeof value.updatedAt === 'number' ? value.updatedAt : now;
      if (now - updatedAt > THREAD_NAVIGATION_HANDOFF_STALE_MS) continue;
      next.set(threadId, {
        ...(value as ThreadNavigationHandoff),
        updatedAt,
      });
    }
    return next;
  } catch {
    return new Map();
  }
}

function writeThreadNavigationHandoffsToSessionStorage(
  handoffs: Map<string, ThreadNavigationHandoff>
) {
  if (typeof window === 'undefined') return;

  try {
    if (handoffs.size === 0) {
      window.sessionStorage?.removeItem(THREAD_NAVIGATION_HANDOFF_STORAGE_KEY);
      return;
    }
    window.sessionStorage?.setItem(
      THREAD_NAVIGATION_HANDOFF_STORAGE_KEY,
      JSON.stringify(Object.fromEntries(handoffs))
    );
  } catch {
    // Ignore storage failures. The in-memory map still covers normal SPA nav.
  }
}

export function ThreadStartupProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [pending, setPending] = useState<Map<string, PendingThreadMessage>>(
    () => new Map()
  );
  const pendingRef = useRef(pending);
  const [navigationHandoffs, setNavigationHandoffs] = useState<
    Map<string, ThreadNavigationHandoff>
  >(() => new Map());
  const navigationHandoffsRef = useRef(navigationHandoffs);

  useEffect(() => {
    const restoredPending = readPendingThreadMessagesFromSessionStorage();
    const restoredHandoffs = readThreadNavigationHandoffsFromSessionStorage();
    pendingRef.current = restoredPending;
    navigationHandoffsRef.current = restoredHandoffs;
    setPending(restoredPending);
    setNavigationHandoffs(restoredHandoffs);
  }, []);

  useEffect(() => {
    const delay = threadNavigationHandoffExpiryDelay(
      navigationHandoffs,
      Date.now()
    );
    if (delay === null) return;
    const timeout = window.setTimeout(() => {
      const current = navigationHandoffsRef.current;
      const next = pruneExpiredThreadNavigationHandoffs(current, Date.now());
      if (next.size === current.size) return;
      navigationHandoffsRef.current = next;
      writeThreadNavigationHandoffsToSessionStorage(next);
      setNavigationHandoffs(next);
    }, delay + 1);
    return () => window.clearTimeout(timeout);
  }, [navigationHandoffs]);

  const setPendingForThread = useCallback(
    (threadId: string, data: PendingThreadMessage) => {
      const next = new Map(pendingRef.current);
      next.set(String(threadId), normalizeAutoSendMessage(data));
      pendingRef.current = next;
      writePendingThreadMessagesToSessionStorage(next);
      setPending(next);
    },
    []
  );

  const hasPendingForThread = useCallback((threadId: string) => {
    const key = String(threadId);
    return pendingRef.current.has(key);
  }, []);

  const peekPendingForThread = useCallback(
    (threadId: string): PendingThreadMessage | null => {
      const key = String(threadId);
      return pendingRef.current.get(key) ?? null;
    },
    []
  );

  const consumePendingForThread = useCallback((threadId: string) => {
    const key = String(threadId);
    const value = pendingRef.current.get(key) ?? null;
    if (!pendingRef.current.has(key)) return value;

    const next = new Map(pendingRef.current);
    next.delete(key);
    pendingRef.current = next;
    writePendingThreadMessagesToSessionStorage(next);
    setPending(next);
    return value;
  }, []);

  const setThreadNavigationHandoff = useCallback(
    (threadId: string, data: ThreadNavigationHandoff) => {
      const key = String(threadId);
      if (!key) return;
      const next = new Map(navigationHandoffsRef.current);
      next.set(key, { ...data, updatedAt: Date.now() });
      navigationHandoffsRef.current = next;
      writeThreadNavigationHandoffsToSessionStorage(next);
      setNavigationHandoffs(next);
    },
    []
  );

  const hasThreadNavigationHandoff = useCallback((threadId: string) => {
    const key = String(threadId);
    return navigationHandoffsRef.current.has(key);
  }, []);

  const peekThreadNavigationHandoff = useCallback(
    (threadId: string): ThreadNavigationHandoff | null => {
      const key = String(threadId);
      return navigationHandoffsRef.current.get(key) ?? null;
    },
    []
  );

  const consumeThreadNavigationHandoff = useCallback((threadId: string) => {
    const key = String(threadId);
    const value = navigationHandoffsRef.current.get(key) ?? null;
    if (!navigationHandoffsRef.current.has(key)) return value;

    const next = new Map(navigationHandoffsRef.current);
    next.delete(key);
    navigationHandoffsRef.current = next;
    writeThreadNavigationHandoffsToSessionStorage(next);
    setNavigationHandoffs(next);
    return value;
  }, []);

  const ctx = useMemo<ThreadStartupContextValue>(
    () => ({
      navigationHandoffs,
      setPendingForThread,
      hasPendingForThread,
      peekPendingForThread,
      consumePendingForThread,
      setThreadNavigationHandoff,
      hasThreadNavigationHandoff,
      peekThreadNavigationHandoff,
      consumeThreadNavigationHandoff,
    }),
    [
      navigationHandoffs,
      setPendingForThread,
      hasPendingForThread,
      peekPendingForThread,
      consumePendingForThread,
      setThreadNavigationHandoff,
      hasThreadNavigationHandoff,
      peekThreadNavigationHandoff,
      consumeThreadNavigationHandoff,
    ]
  );

  return (
    <ThreadStartupContext.Provider value={ctx}>
      {children}
    </ThreadStartupContext.Provider>
  );
}

export function useThreadStartup(): ThreadStartupContextValue {
  const ctx = useContext(ThreadStartupContext);
  if (!ctx)
    throw new Error(
      'useThreadStartup must be used within ThreadStartupProvider'
    );
  return ctx;
}
